import { useCallback, useEffect, useState } from "react";
import { useUIStore } from "@/stores/ui";
import type { MessageSummary } from "@/types";
import { useChatStore } from "../stores/chat";

type ReviewDialog =
  | { kind: "intake"; chatId: string; batchId: string; recordCount: number }
  | { kind: "conversation"; chatId: string; messageId: number };

export function useReviewDialogs(surface: "panel" | "page") {
  const [reviewDialog, updateReviewDialog] = useState<ReviewDialog | null>(
    null,
  );
  const [summaryDialog, setSummaryDialog] = useState<MessageSummary | null>(
    null,
  );

  useEffect(() => {
    const clear = () => {
      updateReviewDialog(null);
      setSummaryDialog(null);
    };
    const unsubscribeChat = useChatStore.subscribe((current, previous) => {
      const last = current.messages.at(-1);
      const newUpload =
        last !== previous.messages.at(-1) &&
        last?.role === "user" &&
        Boolean(last.fileAttachments?.length);
      if (
        current.chatId !== previous.chatId ||
        current.chatGeneration !== previous.chatGeneration ||
        newUpload
      )
        clear();
    });
    const unsubscribePanel =
      surface === "panel"
        ? useUIStore.subscribe((current, previous) => {
            if (previous.aiPanelOpen && !current.aiPanelOpen) clear();
          })
        : undefined;
    return () => {
      unsubscribeChat();
      unsubscribePanel?.();
    };
  }, [surface]);

  const setReviewDialog = useCallback(
    (review: ReviewDialog | null) => {
      if (
        review &&
        (review.chatId !== useChatStore.getState().chatId ||
          (surface === "panel" && !useUIStore.getState().aiPanelOpen))
      )
        return;
      updateReviewDialog(review);
    },
    [surface],
  );

  return { reviewDialog, setReviewDialog, summaryDialog, setSummaryDialog };
}
